import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './UploadsPanel.module.css';
import {
  driveDelete,
  driveUpload,
} from '../../brandingMarketing/driveApi';
import { getFallbackThumbDataUrl } from '../utils/thumbUtils';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'image', label: 'Images' },
  { id: 'svg', label: 'SVG' },
  { id: 'pdf', label: 'PDF' },
];

const UploadsPanel = ({
  firebase,
  assets,
  loading,
  error,
  connected,
  needsReconnect,
  rootFolderId,
  onRefresh,
  onConnect,
  onPlaceAsset,
  variant = 'full',
  onUploadStart,
  onToggleLibrary,
  onBeginDrag,
  onEndDrag,
}) => {
  const inputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('all');
  const [dragActive, setDragActive] = useState(false);
  const [notice, setNotice] = useState('');
  const [progressMap, setProgressMap] = useState({});
  const showControls = variant !== 'library';
  const showDropzone = variant !== 'library';
  const showTabs = variant !== 'controls';
  const showGrid = variant !== 'controls';

  const showNotice = useCallback((message) => {
    setNotice(message);
    if (message) {
      window.setTimeout(() => setNotice(''), 3200);
    }
  }, []);

  const filteredAssets = useMemo(() => {
    if (!assets) return [];
    if (activeTab === 'all') return assets;
    return assets.filter((asset) => (asset.type || 'image') === activeTab);
  }, [activeTab, assets]);

  const startUpload = useCallback(
    async (file) => {
      if (!firebase || !rootFolderId || !file) return;
      const maxSize = 25 * 1024 * 1024;
      const allowed = [
        'image/png',
        'image/jpeg',
        'image/webp',
        'image/svg+xml',
        'application/pdf',
      ];
      if (!allowed.includes(file.type) || file.size > maxSize) {
        showNotice('Unsupported file type or size.');
        return;
      }
      const tempId = `${Date.now()}-${file.name}`;
      setProgressMap((prev) => ({ ...prev, [tempId]: 5 }));
      try {
        await driveUpload(firebase, rootFolderId, file, { rootType: 'studio' });
        setProgressMap((prev) => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
        if (onRefresh) {
          await onRefresh();
        }
      } catch (err) {
        console.error('Drive upload failed:', err);
        showNotice(err?.message || 'Upload failed.');
        setProgressMap((prev) => {
          const next = { ...prev };
          delete next[tempId];
          return next;
        });
      }
    },
    [firebase, onRefresh, rootFolderId, showNotice],
  );

  const handleFiles = useCallback(
    (fileList) => {
      if (!fileList || !fileList.length) return;
      if (onUploadStart) {
        onUploadStart();
      }
      Array.from(fileList).forEach((file) => {
        startUpload(file);
      });
    },
    [onUploadStart, startUpload],
  );

  const handlePaste = useCallback(
    (event) => {
      if (!event.clipboardData?.items) return;
      const imageItem = Array.from(event.clipboardData.items).find((item) =>
        item.type.startsWith('image/'),
      );
      if (!imageItem) return;
      const blob = imageItem.getAsFile();
      if (!blob) return;
      const file = new File([blob], `pasted-${Date.now()}.png`, {
        type: blob.type || 'image/png',
      });
      if (onUploadStart) {
        onUploadStart();
      }
      startUpload(file);
    },
    [onUploadStart, startUpload],
  );

  useEffect(() => {
    if (!showControls) return () => {};
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste, showControls]);

  const handleRetry = () => {
    showNotice('Please re-upload the file.');
  };

  const handleCancel = async (assetId) => {
    setProgressMap((prev) => {
      const next = { ...prev };
      delete next[assetId];
      return next;
    });
  };

  const handleAssetClick = (asset) => {
    if (!asset || (asset.status && asset.status !== 'ready')) return;
    if (asset.type === 'pdf') {
      showNotice('PDF placement coming soon.');
      return;
    }
    if (onPlaceAsset) {
      onPlaceAsset(asset);
    }
  };

  const handleDelete = async (asset, event) => {
    event?.stopPropagation();
    if (!asset) return;
    const confirmDelete = window.confirm(
      'Delete this upload? Designs using this file may break.',
    );
    if (!confirmDelete) return;
    try {
      await driveDelete(firebase, asset.id, { rootType: 'studio' });
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      showNotice('Unable to delete file. Please try again.');
    }
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleDrop = (event) => {
    if (!showDropzone) return;
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      setDragActive(false);
      handleFiles(files);
    }
  };

  return (
    <div
      className={`${styles.panel} ${variant === 'library' ? styles.panelLibrary : ''}`}
      onDragOver={(event) => {
        if (!showDropzone) return;
        const hasFiles = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (!hasFiles) return;
        event.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={() => {
        if (!showDropzone) return;
        setDragActive(false);
      }}
      onDrop={handleDrop}
    >
      {showControls && (
        <>
          <div className={styles.actionsRow}>
            <button type="button" className={styles.uploadBtn} onClick={handleUploadClick}>
              Upload files
            </button>
            {onToggleLibrary && (
              <button
                type="button"
                className={styles.libraryBtn}
                onClick={onToggleLibrary}
                title="Open uploads library"
                aria-label="Open uploads library"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="4" y="5" width="7" height="7" rx="2" />
                  <rect x="13" y="5" width="7" height="7" rx="2" />
                  <rect x="4" y="14" width="7" height="7" rx="2" />
                  <rect x="13" y="14" width="7" height="7" rx="2" />
                </svg>
              </button>
            )}
          </div>
          {showDropzone && (
            <div className={`${styles.dropzone} ${dragActive ? styles.dropActive : ''}`}>
              Drag and drop files here
            </div>
          )}
          {!connected && (
            <div className={styles.notice}>
              Connect Google Drive to upload files.
              <button
                type="button"
                className={styles.retryBtn}
                onClick={onConnect}
                style={{ marginLeft: '0.5rem' }}
              >
                Connect
              </button>
            </div>
          )}
          {needsReconnect && connected && (
            <div className={styles.notice}>
              Drive access expired. Please reconnect.
              <button
                type="button"
                className={styles.retryBtn}
                onClick={onConnect}
                style={{ marginLeft: '0.5rem' }}
              >
                Reconnect
              </button>
            </div>
          )}
        </>
      )}
      {showTabs && (
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {notice && <div className={styles.notice}>{notice}</div>}
      {error && <div className={styles.error}>{error}</div>}

      {showGrid && (
        <>
          {loading ? (
            <div className={styles.grid}>
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={`skeleton-${index}`} className={styles.skeletonTile} />
              ))}
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>+</div>
              <p>Upload your brand photos, property images, logos, and more.</p>
            </div>
          ) : (
            <div className={styles.grid}>
              {filteredAssets.map((asset) => {
                const progress = progressMap[asset.id] ?? 0;
                const typeLabel = (asset.type || 'image');
                const thumbUrl = asset.thumbUrl || getFallbackThumbDataUrl(typeLabel);
                const isUploading = asset.status === 'uploading';
                const isFailed = asset.status === 'failed';
                const isCanceled = asset.status === 'canceled';
                const isPdf = typeLabel === 'pdf';
                return (
                  <div
                    key={asset.id}
                    role="button"
                    tabIndex={0}
                    className={styles.tile}
                    aria-disabled={isPdf || (asset.status && asset.status !== 'ready')}
                    onClick={() => handleAssetClick(asset)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleAssetClick(asset);
                      }
                    }}
                    draggable={false}
                    onPointerDown={(event) => {
                      if (event.button !== 0) return;
                      if (isPdf || (asset.status && asset.status !== 'ready')) return;
                      try {
                        event.currentTarget.setPointerCapture(event.pointerId);
                      } catch (error) {
                        // Ignore pointer capture errors.
                      }
                      if (onBeginDrag) onBeginDrag(asset, event);
                    }}
                  >
                    <div
                      className={styles.thumb}
                      style={{ backgroundImage: `url(${thumbUrl})` }}
                    />
                    <div className={styles.tileMeta}>
                      <span>{asset.originalName || 'Upload'}</span>
                    </div>
                    <span className={styles.typeBadge}>{typeLabel.toUpperCase()}</span>
                    {asset.status !== 'uploading' && (
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={(event) => handleDelete(asset, event)}
                        title="Delete upload"
                      >
                        Delete
                      </button>
                    )}
                    <div className={styles.hoverOverlay}>
                      {isPdf ? 'PDF placement soon' : 'Click to add'}
                    </div>

                    {isUploading && (
                      <div className={styles.progressOverlay}>
                        <div className={styles.progressLabel}>
                          Uploading {progress}%
                        </div>
                        <div className={styles.progressBar}>
                          <div style={{ width: `${progress}%` }} />
                        </div>
                        <button
                          type="button"
                          className={styles.cancelBtn}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCancel(asset.id);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {isFailed && (
                      <div className={styles.errorOverlay}>
                        <div>Upload failed</div>
                        <button
                          type="button"
                          className={styles.retryBtn}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRetry(asset.id);
                          }}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {isCanceled && (
                      <div className={styles.errorOverlay}>
                        <div>Canceled</div>
                        <button
                          type="button"
                          className={styles.retryBtn}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRetry(asset.id);
                          }}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        className={styles.fileInput}
        multiple
        accept=".png,.jpg,.jpeg,.webp,.svg,.pdf"
        onChange={(event) => {
          const { files } = event.target;
          if (files?.length) {
            handleFiles(files);
          }
          event.target.value = '';
        }}
      />
    </div>
  );
};

export default UploadsPanel;
